<script setup lang="ts">
import { computed } from "vue";

import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";
import { Material } from "@/types/Views/MaterialDatabase/Material";

import CompleteMappingView from "./CompleteMappingView.vue";
import MaterialDetailView from "./MaterialDetailView.vue";

// Unified material modal. It serves two entry points:
//  - "complete" mode: an EPD/construction was just dropped onto one or more
//    unmapped materials (props `library` + `materials` [+ `mappedMaterialId`]).
//    Renders the focused "set missing properties to complete mapping" flow.
//  - "edit" mode: an already-mapped material is being inspected/edited
//    (props `material` + `materialId`). Renders the tabbed detail view.
// byType defaults to undefined, not to Vue's usual absent-boolean false, so a caller that leaves it
// out reaches CompleteMappingView's own resolveByType fallback instead of forcing byType=false.
const props = withDefaults(
  defineProps<{
    // complete / drop flow — `library` is the EPD/construction that was dropped
    mappedMaterialId?: string;
    materials?: SpeckleMaterial[];
    library?: Material | null;
    /** Grouping the `materials` ids belong to, when the caller knows it (the 3D panels pass byType ids). */
    byType?: boolean;
    // edit / detail flow
    material?: SpeckleMaterial | null;
    materialId?: string;
    /** The inventory group this material was opened from, so the detail header can name it. */
    parentName?: string | null;
    /** What that group is in the current grouping — "Material" or "Type". */
    parentLabel?: string | null;
  }>(),
  { byType: undefined }
);

// A dropped `library` (EPD or construction) is only present in the complete flow.
const isEditMode = computed(() => !props.library && !!props.material);
</script>

<template>
  <MaterialDetailView
    v-if="isEditMode"
    :material="material!"
    :material-id="materialId!"
    :parent-name="parentName"
    :parent-label="parentLabel" />
  <CompleteMappingView
    v-else
    :materials="materials ?? []"
    :library="library"
    :by-type="byType"
    :mapped-material-id="mappedMaterialId" />
</template>
